import { PersistedWorkflow } from '@cotera/sdk/core';
import { containsTemplateRef } from '@cotera/utilities';
import type { ToolCallUpstreamSample } from '../../../hooks/use-tool-call-upstream-samples';
import {
  toolInputRefIssues,
  type ToolInputRefIssue,
} from './tool-input-ref-issues';

const { WorkflowNodeType } = PersistedWorkflow;

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** A binding map, keeping only the entries that actually name a ref. */
const readBindings = (value: unknown): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [key, entry] of Object.entries(asRecord(value))) {
    if (typeof entry === 'string' && entry.trim() !== '') {
      out[key] = entry;
    }
  }
  return out;
};

/** Splits a mapper's fields into whole-value refs and `{{ref}}` templates. */
const splitMapperFields = (
  fields: Readonly<Record<string, string>>
): {
  readonly params: Readonly<Record<string, unknown>>;
  readonly bindings: Readonly<Record<string, string>>;
} => {
  const params: Record<string, unknown> = {};
  const bindings: Record<string, string> = {};
  for (const [key, ref] of Object.entries(fields)) {
    if (containsTemplateRef(ref)) {
      params[key] = ref;
    } else {
      bindings[key] = ref;
    }
  }
  return { params, bindings };
};

/**
 * Where a node of this type keeps the refs it reads from upstream.
 *
 * The same three fields the server validates, and for the same reason: they are
 * the ones resolved through `resolveToolCallParamBindings` at run time. A
 * tool-call node also holds refs as `{{token}}`s inside `params` — which is
 * where a dataset write keeps every one of its — so its params come along too.
 *
 * Anything else contributes nothing. A filter's `path` is deliberately left
 * out: it is checked against its own upstream by the filter editor, and its
 * legacy bare-pointer form would read here as a ref to nothing.
 */
export function nodeUpstreamRefs(node: {
  readonly type: string;
  readonly data?: unknown;
}): {
  readonly params: Readonly<Record<string, unknown>>;
  readonly bindings: Readonly<Record<string, string>>;
} {
  const data = asRecord(node.data);
  switch (node.type) {
    case WorkflowNodeType.ToolCall:
      return {
        params: asRecord(data['params']),
        bindings: readBindings(data['paramBindings']),
      };
    case WorkflowNodeType.EmitEvent:
      return { params: {}, bindings: readBindings(data['payloadBindings']) };
    case WorkflowNodeType.RunWorkflow:
      return { params: {}, bindings: readBindings(data['inputBindings']) };
    case WorkflowNodeType.Mapper:
      // A mapper field is either a whole-value ref or a `{{ref}}` template.
      // Templates go through `params`, which is the side that walks tokens —
      // read as a binding, a template's source segment would be the text
      // before its first slash, and every one would report as a broken ref.
      return splitMapperFields(readBindings(data['fieldBindings']));
    default:
      return { params: {}, bindings: {} };
  }
}

/** True when this node holds nothing that could go stale. */
export function nodeHasUpstreamRefs(node: {
  readonly type: string;
  readonly data?: unknown;
}): boolean {
  const { params, bindings } = nodeUpstreamRefs(node);
  return Object.keys(bindings).length > 0 || Object.keys(params).length > 0;
}

/**
 * The refs this node holds that no longer read anything, given what is upstream
 * of it.
 *
 * The same check the open drawer runs, against the same samples — so a node
 * marked on the canvas is a node whose drawer says why, in the same words.
 */
export function nodeRefIssues(
  node: { readonly type: string; readonly data?: unknown },
  upstreamSamples: readonly ToolCallUpstreamSample[]
): readonly ToolInputRefIssue[] {
  const { params, bindings } = nodeUpstreamRefs(node);
  if (Object.keys(params).length === 0 && Object.keys(bindings).length === 0) {
    return [];
  }
  return toolInputRefIssues({ params, bindings, upstreamSamples });
}
